import { useState, useEffect } from 'react'
import { galleryAPI, uploadMultipleFiles, uploadFile } from '../services/api'
import { useNotification } from '../context/NotificationContext'
import { getErrorMessage, getImageUrl, confirmAction } from '../utils/helpers'

const Gallery = () => {
  const { showSuccess, showError } = useNotification()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchGallery()
  }, [])

  const fetchGallery = async () => {
    try {
      const response = await galleryAPI.getAll()
      setItems(response.data.items || [])
    } catch (error) {
      showError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    const files = formData.getAll('images')
    
    if (files.length === 0 || (files.length === 1 && files[0].size === 0)) {
      showError('Choose at least one image to upload.')
      return
    }

    try {
      const caption = formData.get('caption')
      let baseOrder = parseInt(formData.get('sort_order'), 10) || 10

      for (let i = 0; i < files.length; i++) {
        if (files[i].size > 0) {
          const uploadRes = await uploadFile(files[i])
          const uploadedImg = uploadRes.data.filename || uploadRes.data.image
          await galleryAPI.create({
            image: uploadedImg,
            caption,
            sort_order: baseOrder + i,
          })
        }
      }

      showSuccess(`${files.length} image(s) added to the gallery`)
      fetchGallery()
      e.target.reset()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleUpdate = async (e, id, currentImage) => {
    e.preventDefault()
    const formData = new FormData(e.target)
    
    try {
      const data = {
        caption: formData.get('caption'),
        sort_order: formData.get('sort_order'),
        image: currentImage,
      }

      const imageFile = formData.get('image')
      if (imageFile && imageFile.size > 0) {
        const uploadRes = await uploadFile(imageFile)
        data.image = uploadRes.data.filename || uploadRes.data.image
      }

      await galleryAPI.update(id, data)
      showSuccess('Gallery item updated.')
      fetchGallery()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleDelete = async (id) => {
    if (!confirmAction('Delete this gallery item?')) return
    
    try {
      await galleryAPI.delete(id)
      showSuccess('Gallery item deleted.')
      fetchGallery()
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  if (loading) return <div className="admin-page">Loading...</div>

  return (
    <div className="admin-page">
      <h2 className="admin-title">Gallery</h2>
      <p className="admin-sub">Manage your gallery images. Changes appear on the website instantly.</p>

      <div className="row">
        <div className="col-lg-8">
          <div className="panel">
            <div className="panel-head">Existing Gallery Images</div>
            {items.length > 0 ? (
              items.map((item) => (
                <div key={item.id} className="item-row">
                  <div className="item-thumb">
                    <img src={getImageUrl(item.image)} alt={item.caption} />
                  </div>
                  <form onSubmit={(e) => handleUpdate(e, item.id, item.image)} className="item-edit">
                    <input type="number" name="sort_order" defaultValue={item.sort_order} className="form-control form-control-sm" style={{width: '70px'}} title="Order" />
                    <input type="text" name="caption" defaultValue={item.caption} className="form-control form-control-sm item-desc" placeholder="Caption" />
                    <input type="file" name="image" className="form-control form-control-sm item-image" accept="image/*" />
                    <button className="btn btn-sm btn-gold" title="Save"><i className="fa fa-check"></i></button>
                  </form>
                  <button onClick={() => handleDelete(item.id)} className="btn btn-sm btn-danger" title="Delete">
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              ))
            ) : (
              <p className="muted">No gallery images yet.</p>
            )}
          </div>
        </div>

        <div className="col-lg-4">
          <div className="panel">
            <div className="panel-head">Add Gallery Images</div>
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>Images * (up to 10)</label>
                <input type="file" name="images" className="form-control-file" accept="image/*" multiple required />
              </div>
              <div className="form-group">
                <label>Caption (optional)</label>
                <input type="text" name="caption" className="form-control" />
              </div>
              <div className="form-group">
                <label>Starting order</label>
                <input type="number" name="sort_order" className="form-control" defaultValue="10" />
              </div>
              <button className="btn btn-gold btn-block">Upload Images</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Gallery
